import { Component, Vue, Prop, Inject } from 'vue-property-decorator';
import { Attendee, createAttendeeFromSelectListItemWithSubText, DtoAttendee } from '../../common/types/event/DtoAttendee';
import { TicketsAndAddons } from '../../common/types/event/DtoTicketsAndAddons';
import PhoneInput from '../../components/inputs/phone/PhoneInput.vue';
import CustomFieldEditor from '../../components/custom-field/CustomFieldEditor.vue';
import { DtoEvent } from '../../common/types/event/DtoEvent';
import AttendeeSelector from './AttendeeSelector.vue';
import { AttendeeSelectListItem, createSelectListItemWithSubtextFromAttendee, SelectListItemWithSubtext } from '../../common/types/SelectListItemWithSubtext';
import { CustomField } from '../../common/types/CustomField/CustomField';
import { TicketMemberField } from '../../common/types/event/TicketMemberField';
import AddressInfo from '../membership/AddressInfo.vue';
import { DtoTicketFieldItem, createDtoTicketFieldItem } from '../../common/types/event/DtoTicketFieldItem';
import FieldValidationErrors from '@/components/errors/FieldValidationErrors.vue';
import InfoAlert from '../../components/errors/InfoAlert.vue';
import { IEventRegistrationGlobals } from '../../pages/event-registration/EventRegistrationGlobals';
import { DtoCurrentUser } from '../../common/types/user/DtoCurrentUser';
import Credentials from '../../components/membership/Credentials.vue';
import { CustomFieldValue } from '../../common/types/CustomField/CustomFieldValue';
import { DtoEventRegistration } from '../../common/types/event/DtoEventRegistration';
import { eventRepo } from '../../common/repositories/EventRepo';

declare let noviNs: IEventRegistrationGlobals;

@Component({
    name: 'AttendeeForm',
    components: {
        PhoneInput,
        CustomFieldEditor,
        AttendeeSelector,
        AddressInfo,
        FieldValidationErrors,
        InfoAlert,
        Credentials
    }
})
export default class EventAttendeeForm extends Vue {
    @Prop({ required: true })
    attendee!: Attendee;

    @Prop({ required: false, default: null })
    ticketNumber!: Number;

    @Prop({ required: false })
    event!: DtoEvent;

    @Prop({ required: true })
    registrationModel!: DtoEventRegistration;

    @Prop({ required: true, default: () => [] })
    eligibleAttendees!: Attendee[];

    @Prop({ required: false })
    guestNotAllowedMessage!: string;

    @Prop({ required: false, default: () => '' })
    shippingAddressInstructions!: string;

    @Prop({ required: false, default: () => '' })
    parentMemberInstructions!: string;

    @Prop({ required: false, default: () => '' })
    professionalEmailInstructions!: string;

    @Inject('ticketsAndAddons')
    ticketsAndAddons!: TicketsAndAddons;

    @Inject('validator')
    $validator: any;

    @Inject('user')
    currentUser!: DtoCurrentUser;

    isTbdChecked: boolean = this.attendee.isTbd;

    parentLabel: string = noviNs.parentLabel;

    get visibleFields() {
        let fields: DtoTicketFieldItem[] = [];
        var self = this;

        this.attendee.selectedTickets
            .sort((a, b) => a.categoryDisplayOrder - b.categoryDisplayOrder || a.displayOrder - b.displayOrder)
            .forEach((ticket) => {
                ticket.ticketFieldConfig.forEach((fieldItem) => {
                    var existingField = fields.find(x => fieldItem.customField != null
                        ? x.customField != null && x.customField.id == fieldItem.customField.id
                        : x.memberField == fieldItem.memberField);

                    // Enforce 'Requires Unique Emails' setting if applied
                    if (fieldItem.memberField == TicketMemberField.Email && this.event.attendeeBased && this.event.requireUniqueEmailPerAttendee) {
                        fieldItem.isRequired = true;
                    }

                    // Check if we are dealing with the name field on an attendee-centered ticket.
                    var isAttendeeDefaultNameField = this.event.attendeeBased
                        && fieldItem.memberField == TicketMemberField.Name
                        && fieldItem.customField == null;

                    if (existingField && !isAttendeeDefaultNameField) {
                        existingField.isRequired = existingField.isRequired || fieldItem.isRequired;
                    } else if (!isAttendeeDefaultNameField) {
                        fields.push(createDtoTicketFieldItem(fieldItem));
                    }
                });
            });

        this.attendee.selectedAddons
            .filter(a => a.isDonation == false || a.price > 0) // exclude donations with 0 price
            .forEach(function (addon) {
                var product = self.ticketsAndAddons.products.find(p => p.id === addon.id);
                if (product) {
                    product.customFields.forEach(function (customField) {
                        var existingField = fields.find(field => field.customField != null && field.customField.id == customField.id);
                        if (existingField) {
                            existingField.isRequired = existingField.isRequired || customField.requiredFrontend;
                        }
                        else {
                            fields.push(createDtoTicketFieldItem({ customField: customField, isRequired: customField.requiredFrontend }));
                        }
                    });
                }
            });

        return fields;
    }

    get allowGuest() {
        return this.attendee && this.attendee.selectedTickets && this.attendee.selectedTickets.length && this.attendee.selectedTickets[0].allowGuestAttendee;
    }

    get allowTbd() {
        return this.attendee && this.attendee.selectedTickets[0].ticketFieldConfig.some(x => x.memberField == TicketMemberField.Name && x.customField == null && x.isRequired == false);
    }

    get showHeader() {
        return this.event.attendeeBased && this.visibleFields.length > 0;
    }

    get showMemberCompassLink() {
        return this.currentUser && this.currentUser.customerID && this.currentUser.customerID > 0 && this.currentUser.customerID == this.attendee.customerId && this.visibleFields.length > 0;
    }

    get isTbd() {
        return this.allowTbd && ((this.attendee.name == noviNs.tbdEventName && this.event.attendeeBased) || (this.isTbdChecked && !this.event.attendeeBased));
    }

    isFieldVisible(field: DtoTicketFieldItem, fieldName: string) {
        if (field.customField != null) {
            return false;
        }

        var fieldVal = (<any>TicketMemberField)[fieldName];

        // never show name field on attendee based 
        if (fieldVal == TicketMemberField.Name && this.event.attendeeBased) {
            return false;
        }

        return field.memberField == fieldVal;
    }

    isCustomFieldVisible(field: DtoTicketFieldItem, customField: CustomField) {
        if (field.customField == null || customField == null) {
            return false;
        }

        return field.customField.id == customField.id;
    }

    getCustomFieldValue(fieldId: number) {
        let checkValue = this.attendee.attendeeCustomFieldValues[fieldId.toString()] ||
            this.attendee.customerCustomFieldValues[fieldId.toString()];
        if (typeof (checkValue) == 'undefined') {
            let newFieldValue = <CustomFieldValue>{};
            newFieldValue.customFieldId = fieldId;
            this.attendee.attendeeCustomFieldValues[fieldId.toString()] = newFieldValue;
            return newFieldValue;
        }
        return checkValue;
    }

    onAttendeeUpdated(attendee: SelectListItemWithSubtext) {
        this.$emit("update:attendee", attendee);
    }

    validateRequired(isRequired: boolean) {
        return isRequired ? "required" : "";
    }

    validateEmailRequired(isRequired: boolean) {
        const rules: any = { email: true };

        if (isRequired || this.event.requireUniqueEmailPerAttendee) {
            rules.required = true;
        }

        // Add the 'uniqueEmail' rule if the event setting is enabled
        if (this.event.requireUniqueEmailPerAttendee) {
            rules.uniqueEmail = { attendees: this.registrationModel.attendees };
        }

        return rules;
    }

    tbdCheckChanged() {
        if (this.isTbd) {
            this.attendee.setAsTbd();
            this.$validator.reset();
            this.$root.$emit('reset:attendee', false);
        } else {
            this.attendee.name = "";
            this.$root.$emit('reset:attendee', true);
        }
    }

    private onEmailChange(newEmail: string): void {
        this.$emit('update-email-to-validate', newEmail);
        this.$emit('check-unique-email', this.$validator);
    }
}